import { useId } from 'react';
import '../../styles/reportsTo.css';

// Migration 259: "Reports to (Manager)" is an HR field. Only Owners/Admins or people with Personnel edit rights change it, and
// never for themselves; the database enforces that (staff_reports_to_guard). This field only mirrors the answer: a picker when
// the viewer may change it, otherwise the name with a lock.
export const REPORTS_TO_LABEL = 'Reports to (Manager)';
export const REPORTS_TO_LOCKED_NOTE = 'Locked. Owners, Admins or Personnel editors set this.';

// Active staff of the same company, never the person themselves, sorted by name.
export function reportsToOptions(staff = [], selfId = null) {
    return staff
        .filter((member) => member.id !== selfId && member.status === 'active')
        .map((member) => ({ id: member.id, label: member.full_name || member.title || 'Unnamed staff' }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

export function reportsToName(staff = [], managerId) {
    if (!managerId) return 'No manager';
    const manager = staff.find((member) => member.id === managerId);
    return manager?.full_name || manager?.title || 'A staff member you cannot see';
}

function LockIcon() {
    return (
        <svg className="ia-reports-to__lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
    );
}

export default function ReportsToField({ value = '', onChange, options = [], canChange = false, managerName, disabled = false }) {
    const id = useId();
    const noteId = `${id}-note`;
    if (!canChange) {
        return (
            <div className="ia-reports-to" data-testid="reports-to-locked">
                <span className="ia-reports-to__label" id={`${id}-label`}>{REPORTS_TO_LABEL}</span>
                <div className="ia-reports-to__locked" role="group" aria-labelledby={`${id}-label`} aria-describedby={noteId}>
                    <LockIcon />
                    <span className="ia-reports-to__name">{managerName || 'No manager'}</span>
                </div>
                <span id={noteId} className="ia-reports-to__note">{REPORTS_TO_LOCKED_NOTE}</span>
            </div>
        );
    }
    return (
        <label className="ia-reports-to" htmlFor={id}>
            <span className="ia-reports-to__label">{REPORTS_TO_LABEL}</span>
            <select id={id} className="ia-reports-to__select" value={value || ''} disabled={disabled}
                onChange={(event) => onChange?.(event.target.value)}>
                <option value="">No manager</option>
                {value && !options.some((option) => option.id === value) && (
                    <option value={value}>{managerName || 'Current manager'}</option>
                )}
                {options.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
            <span className="ia-reports-to__note">HR record. The person can see this but cannot change it.</span>
        </label>
    );
}
